import React, { useCallback, useState } from 'react';
import { useDropzone } from 'react-dropzone';
import styled from 'styled-components';
import { FiUploadCloud, FiX, FiTrash2 } from 'react-icons/fi';
import apiClient from '../services/api';
import { toast } from 'react-toastify';

const UploadSection = styled.div`
  margin-bottom: 30px;
`;

const SectionTitle = styled.h3`
  color: #424242;
  margin-bottom: 15px;
  font-size: 1.1rem;
  font-weight: 600;
  display: flex;
  align-items: center;
  gap: 8px;
`;

const DropzoneArea = styled.div`
  border: 2px dashed ${props => props.$isDragActive ? '#667eea' : '#d0d0d0'};
  border-radius: 12px;
  padding: 40px 20px;
  text-align: center;
  background: ${props => props.$isDragActive ? '#f3f8ff' : '#fafafa'};
  cursor: pointer;
  transition: all 0.3s ease;
  
  &:hover {
    background: #f3f8ff;
    border-color: #667eea;
  }
  
  svg {
    font-size: 3rem;
    color: #667eea;
    margin-bottom: 15px;
  }
  
  p {
    color: #616161;
    font-size: 1rem;
    margin: 8px 0;
  }
  
  .formats {
    color: #9e9e9e;
    font-size: 0.85rem;
    margin-top: 8px;
  }
`;

const FileList = styled.div`
  margin-top: 20px;
  
  .list-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 12px;
    
    h4 {
      color: #424242;
      margin: 0;
      font-size: 0.95rem;
      font-weight: 600;
    }
  }
  
  .file-item {
    background: #f9f9f9;
    border: 1px solid #e0e0e0;
    border-radius: 8px;
    padding: 12px 15px;
    margin: 8px 0;
    display: flex;
    justify-content: space-between;
    align-items: center;
    transition: all 0.3s ease;
    
    &:hover {
      background: #f3f8ff;
      border-color: #667eea;
    }
  }
  
  .file-info {
    display: flex;
    flex-direction: column;
    gap: 4px;
    flex: 1;
  }
  
  .file-name {
    font-weight: 600;
    color: #424242;
    font-size: 0.9rem;
  }
  
  .file-size {
    color: #9e9e9e;
    font-size: 0.8rem;
  }
`;

const SmallButton = styled.button`
  background: ${props => props.$variant === 'danger' ? '#fff' : props.$variant === 'primary' ? '#667eea' : '#f5f5f5'};
  color: ${props => props.$variant === 'danger' ? '#f44336' : props.$variant === 'primary' ? '#fff' : '#616161'};
  border: ${props => props.$variant === 'danger' ? '1px solid #f44336' : props.$variant === 'primary' ? 'none' : '1px solid #e0e0e0'};
  padding: 6px 12px;
  font-size: 0.8rem;
  font-weight: 600;
  font-family: 'Segoe UI', sans-serif;
  cursor: pointer;
  border-radius: 6px;
  display: flex;
  align-items: center;
  gap: 4px;
  transition: all 0.3s ease;
  
  &:hover {
    transform: translateY(-1px);
    box-shadow: 0 2px 8px ${props => 
      props.$variant === 'danger' ? 'rgba(244, 67, 54, 0.3)' : 
      props.$variant === 'primary' ? 'rgba(102, 126, 234, 0.3)' : 
      'rgba(0, 0, 0, 0.1)'};
    background: ${props => props.$variant === 'danger' ? '#f44336' : props.$variant === 'primary' ? '#764ba2' : '#eeeeee'};
    color: ${props => props.$variant === 'danger' || props.$variant === 'primary' ? '#fff' : '#424242'};
  }
  
  svg {
    font-size: 0.9rem;
  }
`;

const UploadButton = styled.button`
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: #fff;
  border: none;
  padding: 12px 30px;
  font-size: 0.95rem;
  font-weight: 600;
  font-family: 'Segoe UI', sans-serif;
  cursor: pointer;
  border-radius: 8px;
  margin-top: 15px;
  transition: all 0.3s ease;
  width: 100%;
  
  &:hover:not(:disabled) {
    transform: translateY(-2px);
    box-shadow: 0 6px 20px rgba(102, 126, 234, 0.4);
  }
  
  &:disabled {
    background: #bdbdbd;
    cursor: not-allowed;
  }
`;

const UploadedBadge = styled.span`
  background: #4caf50;
  color: #fff;
  padding: 4px 10px;
  border-radius: 4px;
  font-size: 0.75rem;
  font-weight: 600;
`;

const FileUpload = ({ onFilesUploaded, uploadedFiles, onUploadStart }) => {
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [uploading, setUploading] = useState(false);

  const onDrop = useCallback((acceptedFiles) => {
    setSelectedFiles(prev => [...prev, ...acceptedFiles]);
    toast.info(`${acceptedFiles.length} file(s) added`);
  }, []);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      'application/vnd.ms-excel': ['.xls'],
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': ['.xlsx'],
      'text/csv': ['.csv'],
      'text/plain': ['.txt']
    }
  });

  const removeFile = (indexToRemove) => {
    setSelectedFiles(prev => prev.filter((_, index) => index !== indexToRemove));
    toast.info('File removed');
  };

  const clearAllFiles = () => {
    setSelectedFiles([]);
    toast.info('All files cleared');
  };

  const handleUpload = async () => {
    if (selectedFiles.length === 0) {
      toast.error('Please select files first!');
      return;
    }

    setUploading(true);
    if (onUploadStart) {
      onUploadStart();
    }
    
    const formData = new FormData();
    selectedFiles.forEach(file => {
      formData.append('files', file);
    });

    try {
      const response = await apiClient.post('/api/v1/upload', formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });

      onFilesUploaded(response.data.job_id, response.data.files);
      setSelectedFiles([]);
      toast.success('Files uploaded successfully!');
    } catch (error) {
      console.error('Upload error:', error);
      
      let errorMessage = 'Upload failed!';
      if (error.code === 'ECONNREFUSED' || error.code === 'ERR_NETWORK') {
        errorMessage = '⚠️ Cannot connect to backend server. Is it running on port 8000?';
      } else if (error.response) {
        errorMessage = error.response.data?.detail || error.message;
      }
      
      toast.error(errorMessage);
    } finally {
      setUploading(false);
    }
  };

  return (
    <UploadSection>
      <SectionTitle>📁 File Upload</SectionTitle>
      
      {!uploadedFiles || uploadedFiles.length === 0 ? (
        <>
          <DropzoneArea {...getRootProps()} $isDragActive={isDragActive}>
            <input {...getInputProps()} />
            <FiUploadCloud />
            {isDragActive ? (
              <p>Drop the files here...</p>
            ) : (
              <>
                <p>Drag & drop files here, or click to select</p>
                <p className="formats">Supported: Excel (.xlsx, .xls), CSV, TXT</p>
              </>
            )}
          </DropzoneArea>

          {selectedFiles.length > 0 && (
            <FileList>
              <div className="list-header">
                <h4>Selected Files ({selectedFiles.length})</h4>
                <SmallButton onClick={clearAllFiles}>
                  <FiTrash2 /> Clear All
                </SmallButton>
              </div>
              
              {selectedFiles.map((file, index) => (
                <div key={index} className="file-item">
                  <div className="file-info">
                    <span className="file-name">📄 {file.name}</span>
                    <span className="file-size">
                      {(file.size / 1024).toFixed(2)} KB
                    </span>
                  </div>
                  <SmallButton $variant="danger" onClick={() => removeFile(index)}>
                    <FiX /> Remove
                  </SmallButton>
                </div>
              ))}
              
              <UploadButton onClick={handleUpload} disabled={uploading}>
                {uploading ? '⏳ Uploading...' : '🚀 Upload Files'}
              </UploadButton>
            </FileList>
          )}
        </>
      ) : (
        <FileList>
          <div className="list-header">
            <h4>✅ Uploaded Files ({uploadedFiles.length})</h4>
          </div>
          
          {uploadedFiles.map((file, index) => (
            <div key={index} className="file-item">
              <div className="file-info">
                <span className="file-name">📄 {file.filename}</span>
                <span className="file-size">
                  {(file.size / 1024).toFixed(2)} KB
                </span>
              </div>
              <UploadedBadge>Uploaded</UploadedBadge>
            </div>
          ))}
        </FileList>
      )}
    </UploadSection>
  );
};

export default FileUpload;